import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Panel } from '@tanium/react-panel';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import { FieldRendererProps, RibbonColumn, StackedRibbonList } from '@tanium/react-stacked-ribbon-list';
import { useTranslation } from 'react-i18next';
import { TooltippedIconButton } from '@tanium/react-icon-button';
import { EditIcon, TrashIcon, AddIcon } from '@tanium/react-graphics';
import { useDispatch, useSelector } from 'react-redux';
import { Stack } from '@tanium/react-stack';
import { Link } from '@tanium/react-link';
import { K8sRoleTargetAccess } from 'gql/K8sRoleTargetAccess';
import ConfirmationModal from 'components/pages/request/requestPartials/ConfirmationModal';
import K8sRoleTargetAccessSlice from 'components/pages/console/store/K8sRoleTargetAccessSlice';
import K8sRoleTargetAccessSelectors from 'components/pages/console/store/K8sRoleTargetAccessSelectors';
import InlineCopyToClipboard from 'components/common/copyToClipboard/inlineCopyToClipboard';
import K8sRoleTargetAccessCRUDModal, {
  Operation,
} from 'components/common/K8sRoleTargetAccess/K8sRoleTargetAccessCRUDModal';
import Helper from 'utils/Helper';
import { TARGET_GROUP_ROUTE } from 'Route';
import { FilterInput } from '@tanium/react-filter-input';
import { FormRow } from '@tanium/react-form';

export interface IK8sRoleAccessListProps {
  targetOrGroupID: string;
  canEdit: boolean;
  includeAllTargetGroups: boolean;
}

export const K8sRoleAccessList = ({
  targetOrGroupID,
  canEdit,
  includeAllTargetGroups,
}: IK8sRoleAccessListProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [filterListText, setFilterListText] = useState('');

  const textListFilter = (
    <FormRow label="">
      <FilterInput
        style={{ height: '100%' }}
        placeholder={t('FILTER_BY_TEXT')}
        onChange={({ value }) => setFilterListText(value)}
        value={filterListText}
      />
    </FormRow>
  );

  const getFilteredList = useCallback(
    (s: K8sRoleTargetAccess[]): K8sRoleTargetAccess[] => {
      return s.filter((item) =>
        JSON.stringify(Object.values(item)).toLowerCase().includes(filterListText.toLowerCase())
      );
    },
    [filterListText]
  );

  const [selectedItem, setSelectedK8sRole] = useState<K8sRoleTargetAccess | undefined>(undefined);
  const targetAccessList = useSelector(K8sRoleTargetAccessSelectors.selectK8sRoleTargetAccessList);
  const mutationSucceeded = useSelector(K8sRoleTargetAccessSelectors.selectMutationSucceeded);
  const loading = useSelector(K8sRoleTargetAccessSelectors.selectK8sRoleTargetAccessLoading);
  const filteredList = getFilteredList(targetAccessList);
  const dataGetter = useCallback((index: number) => filteredList[index], [filteredList]);
  const itemIdGetter = useCallback((index) => dataGetter(index).id, [dataGetter]);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);

  useEffect(() => {
    dispatch(
      K8sRoleTargetAccessSlice.actions.listK8sRoleTargetAccess({
        targetOrTargetGroupID: targetOrGroupID,
        includeAllTargetGroups,
      })
    );

    return () => {
      dispatch(K8sRoleTargetAccessSlice.actions.clearState());
    };
  }, [targetOrGroupID, dispatch, includeAllTargetGroups]);

  const columns: RibbonColumn<K8sRoleTargetAccess>[] = useMemo(
    () => [
      {
        label: 'Actions',
        dataKey: 'actions',
        grow: 0,
        width: 50,
        isSortDisabled: true,
        renderer: ({ itemData, index }: FieldRendererProps<K8sRoleTargetAccess>): React.ReactElement => {
          return canEdit && itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <div>
              <TooltippedIconButton
                data-testid={`k8sRoleTargetAccess-edit-${index}`}
                tooltipContent={t('TARGET_ACCESS_EDIT')}
                label={t('TARGET_ACCESS_EDIT')}
                height={16}
                width={16}
                icon={EditIcon}
                onClick={() => {
                  setSelectedK8sRole(itemData);
                  setIsOpen(true);
                }}
              />
              <TooltippedIconButton
                data-testid={`k8sRoleTargetAccess-delete-${index}`}
                style={{ marginLeft: '0.5em' }}
                tooltipContent={t('TARGET_ACCESS_DELETE')}
                label={t('TARGET_ACCESS_DELETE')}
                height={16}
                width={16}
                icon={TrashIcon}
                onClick={() => {
                  setSelectedK8sRole(itemData);
                  setIsDeleteConfirmationOpen(true);
                }}
              />
            </div>
          ) : (
            <></>
          );
        },
      },
      {
        label: t('K8S_ROLE_TARGET_ACCESS_ROLE_NAME_LABEL'),
        dataKey: 'roleName',
        primary: true,
      },
      {
        label: t('ID'),
        dataKey: 'k8s-role-access-id',
        renderer: ({ itemData }) => {
          return (
            <div>
              <InlineCopyToClipboard
                displayedText={itemData.id}
                copyText={itemData.id}
                iconSize={{ width: 16, height: 16 }}
                tooltipContent={t('COPY_ID')}
              />
            </div>
          );
        },
      },
      {
        label: t('K8S_ROLE_TARGET_ACCESS_NAMESPACE_LABEL'),
        dataKey: 'namespace',
      },
      {
        label: t('DESCRIPTION'),
        dataKey: 'description',
      },
      {
        label: t('AD_GROUPS'),
        dataKey: 'adGroups',
      },
      {
        label: t('K8S_ROLE_TARGET_ACCESS_API_ACCESS_LABEL'),
        dataKey: 'apiAccessType',
        renderer: ({ itemData }) => {
          return t(itemData.apiAccessType);
        },
      },
      {
        label: t('PARENT_ID'),
        dataKey: 'targetOrTargetGroupID',
        renderer: ({ itemData }) => {
          return itemData.targetOrTargetGroupID === targetOrGroupID ? (
            <>{t('SELF')}</>
          ) : (
            <Link
              data-testid={`link-${itemData.id}`}
              href={Helper.interpolateRoute(TARGET_GROUP_ROUTE.url, { targetGroupId: itemData.targetOrTargetGroupID })}
            >
              {itemData.targetOrTargetGroupID}
            </Link>
          );
        },
      },
    ],
    [setIsDeleteConfirmationOpen, setSelectedK8sRole, setIsOpen, targetOrGroupID, t, canEdit]
  );

  const onModalSubmit = (operation: Operation) => {
    setIsOpen(false);
    switch (operation.type) {
      case 'cancel':
        break;
      case 'create':
        dispatch(
          K8sRoleTargetAccessSlice.actions.createK8sRoleTargetAccess({
            adGroups: operation.adGroups,
            description: operation.description,
            namespace: operation.namespace,
            roleName: operation.roleName,
            targetOrTargetGroupID: targetOrGroupID,
            apiAccessType: operation.apiAccessType,
          })
        );
        break;
      case 'update':
        dispatch(
          K8sRoleTargetAccessSlice.actions.updateK8sRoleTargetAccess({
            id: operation.id,
            adGroups: operation.adGroups,
            description: operation.description,
            namespace: operation.namespace,
            roleName: operation.roleName,
            targetOrTargetGroupID: operation.targetOrTargetGroupId,
            apiAccessType: operation.apiAccessType,
          })
        );
        break;
    }
  };

  useEffect(() => {
    if (mutationSucceeded) {
      dispatch(
        K8sRoleTargetAccessSlice.actions.listK8sRoleTargetAccess({
          includeAllTargetGroups,
          targetOrTargetGroupID: targetOrGroupID,
        })
      );
    }
  }, [dispatch, targetOrGroupID, mutationSucceeded, includeAllTargetGroups]);

  const handleDelete = useCallback(
    (proceed: boolean) => {
      if (proceed && selectedItem !== undefined) {
        dispatch(K8sRoleTargetAccessSlice.actions.deleteK8sRoleTargetAccess(selectedItem.id));
      }

      setIsDeleteConfirmationOpen(false);
      setSelectedK8sRole(undefined);
    },
    [setIsDeleteConfirmationOpen, setSelectedK8sRole, selectedItem, dispatch]
  );

  const isItemLoaded = useCallback(
    (index: number) => {
      return !!dataGetter(index);
    },
    [dataGetter]
  );

  const isIdEmphasized = useCallback(
    (id: string) => {
      const item = targetAccessList.find((role) => role.id === id);
      return item?.targetOrTargetGroupID === targetOrGroupID;
    },
    [targetAccessList, targetOrGroupID]
  );

  const createK8sRoleTargetAccess = useCallback(() => {
    setSelectedK8sRole(undefined);
    setIsOpen(true);
  }, [setIsOpen]);

  const listActions = canEdit ? (
    <Stack alignItems="center">
      <Button data-testid="k8sRoleTargetAccess-create" size="sm" onClick={createK8sRoleTargetAccess}>
        <Box pr={1} display={'inline-block'}>
          <AddIcon height={'10px'} />
        </Box>
        {t('CREATE')}
      </Button>
      <div style={{ marginLeft: '0.5em' }}>{textListFilter}</div>
    </Stack>
  ) : (
    <></>
  );

  return (
    <Panel title={t('K8S_ROLE_TARGET_ACCESS_PANEL_TITLE')}>
      <Stack direction="column" itemSpacing={1} data-testid="k8sRoleTargetAccess">
        <StackedRibbonList
          enableRowHighlight={false}
          columns={columns}
          isItemLoaded={isItemLoaded}
          height={filteredList.length > 0 ? '12em' : '7.4em'}
          dataGetter={dataGetter}
          itemCount={filteredList.length}
          itemIdGetter={itemIdGetter}
          isLoading={loading}
          listActions={listActions}
          isIdEmphasized={isIdEmphasized}
        />
        <K8sRoleTargetAccessCRUDModal
          isOpen={isOpen}
          onProcess={onModalSubmit}
          targetOrTargetGroupId={targetOrGroupID}
          k8sRoleTargetAccess={selectedItem}
        />
        <ConfirmationModal
          timeoutSeconds={1}
          headerText={t('K8S_ROLE_TARGET_ACCESS_DELETE_HEADER')}
          confirmationText={t('DELETE')}
          closeModal={handleDelete}
          isOpen={isDeleteConfirmationOpen}
        >
          <>{t('K8S_ROLE_TARGET_ACCESS_DELETE_CONTENT')}</>
        </ConfirmationModal>
      </Stack>
    </Panel>
  );
};

export default K8sRoleAccessList;
